import { Button, Tag, type Column } from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';

import {
  MODEL_TYPE_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  describeModel,
  sortable,
  type AffiliateRow,
  type PlanRow,
  type SortKey,
} from './types.ts';

/**
 * The two tables on §5: the affiliate ledger and the commission rate card.
 *
 * Column factories rather than constants, because the last column of the ledger
 * dispatches a row action and the header text changes with the sort — both
 * close over state that has no business inside the table component.
 */

/**
 * The ledger's columns.
 *
 * Every traffic figure here is the 30-DAY one, which is what the design's
 * headers have always said and what the endpoint finally measures. The lifetime
 * totals are still returned and are shown in the drawer, where "since the
 * partner joined" is the question being asked.
 */
export function affiliateColumns(
  sort: SortKey,
  descending: boolean,
  floorBp: number,
  canWrite: boolean,
  onApprove: (row: AffiliateRow) => void,
  onReview: (row: AffiliateRow) => void,
): Column<AffiliateRow>[] {
  return [
    {
      key: 'affiliate',
      header: sortable('Affiliate', 'NAME', sort, descending),
      render: (a) => (
        <>
          {a.name}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {a.ref} · {a.brand}
            {/* Whether this partner can SIGN IN, on the row rather than only
                in the drawer. It is stated as an ABSENCE — "no portal login" —
                because that is what it is, and because until the API grew a way
                to create one it was the state of every affiliate this console
                had ever filed. Nothing is printed once they can sign in: that
                is the resting state and a badge on every row would train the
                eye past the two that need one. */}
            {a.portal_status === 'NONE' && ' · no portal login'}
            {a.portal_status === 'INVITED' && ' · portal invite not accepted'}
          </div>
        </>
      ),
    },
    {
      key: 'source',
      header: 'Traffic source',
      secondary: true,
      render: (a) => <span className="text-muted">{a.source_kind}</span>,
    },
    {
      key: 'model',
      header: 'Model',
      render: (a) =>
        a.model === null ? (
          // Not a formatting fallback: no live plan row means no commission
          // basis, which is a real finding on an ACTIVE affiliate.
          <span className="text-subtle">No live plan</span>
        ) : (
          <span className="text-muted">{describeModel(a.model, a.revshare_bp, a.cpa_amount)}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <>
          <Tag tone={STATUS_TONE[a.status] ?? 'neutral'}>{STATUS_LABEL[a.status] ?? a.status}</Tag>
          {/* The measured half of the auto-review rule, over the window the rule
              is stated in. Shown only where the affiliate actually has signups —
              conversion is null otherwise, and "0% conversion" on zero traffic
              is a lie about a new partner. */}
          {a.conversion_bp_30d !== null && Number(a.conversion_bp_30d) < floorBp && (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              FTD conversion below {formatRate(floorBp)}
            </div>
          )}
          {a.quality_review_opened_at !== null && (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              review opened {new Date(a.quality_review_opened_at).toLocaleDateString('en-PH')}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'signups',
      header: sortable('Signups 30d', 'SIGNUPS', sort, descending),
      numeric: true,
      render: (a) => (
        <>
          <span className="tabular">{formatCount(Number(a.signups_30d))}</span>
          {/* Lifetime under the window, because both matter and only one is the
              column. A partner with 2,000 lifetime signups and 4 this month is
              a different problem from a new one with 4. */}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {formatCount(Number(a.signups))} lifetime
          </div>
        </>
      ),
    },
    {
      key: 'ftd',
      header: sortable('FTDs 30d', 'FTD', sort, descending),
      numeric: true,
      render: (a) => (
        <>
          <span className="tabular">{formatCount(Number(a.ftd_30d))}</span>
          {/* The denominator matters here for the same reason it does on a rail:
              4 FTDs on 6 signups is not a 67% partner. */}
          {a.conversion_bp_30d !== null && (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatRate(Number(a.conversion_bp_30d))} of signups
            </div>
          )}
        </>
      ),
    },
    {
      key: 'epc',
      header: 'EPC',
      secondary: true,
      numeric: true,
      render: (a) =>
        // Accrued commission ÷ 30-day clicks, computed in the query. An
        // affiliate with no recorded clicks gets an em dash, never ₱0.00 —
        // zero earnings per click and no clicks at all are different findings.
        a.epc === null ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="tabular">{formatPesos(BigInt(a.epc))}</span>
        ),
    },
    {
      key: 'commission',
      header: sortable('Accrued', 'COMMISSION', sort, descending),
      numeric: true,
      render: (a) => (
        <>
          <span className="tabular">{formatPesos(BigInt(a.accrued_commission))}</span>
          {/* What has already been settled, so "accrued" cannot be read as
              "everything this partner has ever earned". */}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {formatPesos(BigInt(a.paid_commission))} paid
          </div>
        </>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (a) => {
        /* "Permission-gated actions are HIDDEN, not disabled." A role holding
           partners.view and not partners.manage reads this screen and decides
           nothing on it, so it is shown no button rather than a dead one. */
        if (!canWrite) return null;

        // "The Approve action exists only for status Pending." Review gets its
        // own action, because the trigger that opens one now actually runs and
        // an affiliate that entered REVIEW needs a way out.
        if (a.status === 'PENDING') {
          return (
            <Button
              small
              aria-label={`Approve ${a.name}`}
              onClick={(e) => {
                // The row itself opens the drawer; without this the click does both.
                e.stopPropagation();
                onApprove(a);
              }}
            >
              Approve
            </Button>
          );
        }
        if (a.status === 'REVIEW') {
          return (
            <Button
              small
              aria-label={`Close the quality review on ${a.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onReview(a);
              }}
            >
              Close review
            </Button>
          );
        }
        return null;
      },
    },
  ];
}

/**
 * The commission rate card. Grouped by plan shape rather than by affiliate,
 * because a plan is dated (`valid_from`/`valid_to`) precisely so that changing
 * a model applies to future cohorts only — the live grouping is what a new
 * cohort would be acquired under.
 */
export const planColumns: Column<PlanRow>[] = [
  {
    key: 'model',
    header: 'Model',
    render: (p) => <Tag tone="accent">{describeModel(p.model, p.revshare_bp, p.cpa_amount)}</Tag>,
  },
  {
    key: 'type',
    header: 'Type',
    secondary: true,
    render: (p) => <span className="text-muted">{MODEL_TYPE_LABEL[p.model] ?? p.model}</span>,
  },
  {
    key: 'terms',
    header: 'Terms',
    render: (p) => (
      <>
        <span className="text-muted">
          {p.model === 'REVSHARE' && p.revshare_bp !== null
            ? `${formatRate(Number(p.revshare_bp))} of net GGR, lifetime`
            : null}
          {p.model === 'CPA' && p.cpa_amount !== null
            ? `${formatPesos(BigInt(p.cpa_amount))} per FTD, held ${p.cpa_hold_days} days`
            : null}
          {p.model === 'HYBRID' && p.cpa_amount !== null && p.revshare_bp !== null
            ? `${formatPesos(BigInt(p.cpa_amount))} per FTD plus ${formatRate(Number(p.revshare_bp))} revshare, held ${p.cpa_hold_days} days`
            : null}
        </span>
        {/* The module rule says a CPA carries a qualifying floor.
            `cpa_min_deposit` is the column that holds it; it is reported as
            unset where it is, rather than printed from the design's copy. A
            plan set through this console can now carry one. */}
        {p.model !== 'REVSHARE' && (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {p.cpa_min_deposit === null
              ? 'No qualifying minimum deposit set on this plan'
              : `${formatPesos(BigInt(p.cpa_min_deposit))} minimum deposit`}
          </div>
        )}
      </>
    ),
  },
  {
    key: 'affiliates',
    header: 'Affiliates',
    numeric: true,
    render: (p) => <span className="tabular">{formatCount(Number(p.affiliates))}</span>,
  },
];

/* ── Settlement periods ────────────────────────────────────────────────────
   The affiliate twin of the agent screen's period table. Built as a factory
   rather than a constant because the last column opens the payout run, and a
   column file that reaches for a screen's setter is a column file that can only
   ever be used by one screen. */

/** One row of GET /api/admin/affiliates/periods. */
export interface AffiliatePeriodRow {
  id: string;
  ref: string;
  period_start: string;
  period_end: string;
  /** OPEN → CALCULATED → PAID. A period freezes once it is PAID. */
  status: string;
  calculated_at: string | null;
  paid_at: string | null;
  affiliate_count: string;
  net_revenue: string;
  commission: string;
  /** The CPA bounties this statement settled, broken out of the total. */
  cpa_commission: string;
  /** Commission accrued but withheld — how a suspended affiliate still earns. */
  held: string;
}

const PERIOD_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  CALCULATED: 'Calculated',
  PAID: 'Paid',
};

const periodDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-PH', {
    day: '2-digit',
    month: 'short',
    timeZone: 'Asia/Manila',
  });

export const affiliatePeriodColumns = (
  openRun: (id: string) => void,
): Column<AffiliatePeriodRow>[] => [
  {
    key: 'period',
    header: 'Period',
    render: (p) => (
      <>
        <span className="tabular">{p.ref}</span>
        <div className="text-subtle" style={{ marginTop: 'var(--space-1)' }}>
          {periodDay(p.period_start)} – {periodDay(p.period_end)}
        </div>
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => (
      <Tag tone={p.status === 'PAID' ? 'accent' : p.status === 'OPEN' ? 'quiet' : 'neutral'}>
        {PERIOD_STATUS_LABEL[p.status] ?? p.status}
      </Tag>
    ),
  },
  {
    key: 'affiliates',
    header: 'Affiliates',
    secondary: true,
    numeric: true,
    render: (p) => <span className="tabular">{formatCount(Number(p.affiliate_count))}</span>,
  },
  {
    key: 'net',
    header: 'Net revenue',
    secondary: true,
    numeric: true,
    render: (p) => <span className="tabular">{formatPesosCompact(BigInt(p.net_revenue))}</span>,
  },
  {
    key: 'commission',
    header: 'Commission',
    numeric: true,
    render: (p) => <span className="tabular">{formatPesos(BigInt(p.commission))}</span>,
  },
  {
    key: 'cpa',
    header: 'of which CPA',
    secondary: true,
    numeric: true,
    // Broken out because a bounty is not a share of revenue: it is not offset
    // by a carry-over and a losing period does not claw it back.
    render: (p) =>
      BigInt(p.cpa_commission) === 0n ? (
        <span className="text-subtle">none</span>
      ) : (
        <span className="tabular">{formatPesos(BigInt(p.cpa_commission))}</span>
      ),
  },
  {
    key: 'held',
    header: 'Held',
    numeric: true,
    render: (p) =>
      BigInt(p.held) === 0n ? (
        <span className="text-subtle">none</span>
      ) : (
        <span className="tabular">{formatPesos(BigInt(p.held))}</span>
      ),
  },
  {
    key: 'run',
    header: 'Payout run',
    render: (p) =>
      p.status === 'OPEN' ? (
        <span className="text-subtle">still accruing</span>
      ) : (
        <Button variant="secondary" onClick={() => openRun(p.id)}>
          {p.status === 'PAID' ? 'View payouts' : 'Payout run'}
        </Button>
      ),
  },
];
